/**
 * Company Service
 *
 * Provides CRUD operations for companies — the customer organizations that
 * receive facility services. Companies are the top-level entity that scopes
 * most data in the system: users, locations, work orders, and inspections
 * all belong to a company.
 *
 * Backend endpoints:
 *	 GET	/api/companies		   — List/filter companies
 *	 GET	/api/companies/:id	   — Get a single company
 *	 POST	/api/companies		   — Create a new company
 *	 PATCH	/api/companies/:id	   — Update an existing company
 *	 DELETE /api/companies/:id	   — Delete a company
 *
 * All endpoints require authentication (Sanctum Bearer token).
 */

import { api } from './api';

/**
 * Represents a company as returned by the API.
 * Physical addresses are now stored in the locations table
 * (linked via the company_locations pivot).
 * Only billing address fields remain on the company.
 *
 * @property id - Unique identifier
 * @property name - Legal or display name of the company
 * @property phone - Primary phone number for the company
 * @property email - Primary contact email (used for billing correspondence)
 * @property isActive - Whether this company is currently active in the system
 * @property createdAt - ISO timestamp of when the company was created
 * @property updatedAt - ISO timestamp of the last modification
 */
export interface Company {
	id: string;
	name: string;
	phone: string;
	email: string;
	isActive: boolean;
	createdAt: string;
	updatedAt: string;
}

/**
 * Payload for creating a new company.
 * Physical address should be created as a location
 * via the locations endpoint.
 */
export interface CreateCompanyData {
	name: string;
	phone: string;
	email: string;
}

/**
 * Payload for updating an existing company.
 * All fields are optional (PATCH semantics) — only provided fields are updated.
 * Includes the ability to activate/deactivate a company.
 */
export interface UpdateCompanyData extends Partial<CreateCompanyData> {
	isActive?: boolean;
}

/**
 * Company service methods.
 * Each method maps to a specific backend API endpoint.
 */
export const companyService = {
	/** GET /api/companies — List companies with optional query filters (status, search, etc.) */
	list: (params?: Record<string, string>) =>
		api.get<Company[]>('/companies', params),

	/** GET /api/companies/:id — Fetch a single company by ID with related data */
	getById: (id: string) =>
		api.get<Company>(`/companies/${id}`),

	/** POST /api/companies — Create a new company; returns the created resource */
	create: (data: CreateCompanyData) =>
		api.post<Company>('/companies', data),

	/** PATCH /api/companies/:id — Partially update a company (rename, deactivate, etc.) */
	update: (id: string, data: UpdateCompanyData) =>
		api.patch<Company>(`/companies/${id}`, data),

	/** DELETE /api/companies/:id — Permanently delete a company */
	delete: (id: string) =>
		api.delete<void>(`/companies/${id}`),
};
